import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Search, Trash2, X, Handshake } from "lucide-react";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadErrorState } from "@/components/shared/LoadErrorState";
import { useLeads, useDeleteLead, useUpdateLead, type Lead } from "@/hooks/useLeads";
import { useBudgets } from "@/hooks/useBudgets";
import { useAllContracts } from "@/hooks/useContracts";
import { QuickCreateLeadDialog } from "./QuickCreateLeadDialog";
import { EditLeadDialog } from "./EditLeadDialog";
import { MarkLostDialog } from "./leads/MarkLostDialog";
import { LeadActionsMenu, type LeadActions } from "./leads/LeadActionsMenu";
import { LeadsTable } from "./leads/LeadsTable";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatDistanceToNowStrict, differenceInCalendarDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  deriveStage,
  leadDealValues,
  parsePipelineParams,
  pipelineHeaderCounts,
  stageValueSums,
  STAGE_META,
  FUNNEL_STAGES,
  type PipelineStage,
  type PipelineView,
} from "@/lib/pipelineStage";
import { cn } from "@/lib/utils";
import { barterBadgeLabel } from "@/lib/barter";
import { formatBRL } from "@/lib/format";

const fmt = (v: number) => formatBRL(v, { compact: true });

function shortAge(iso: string) {
  const days = differenceInCalendarDays(new Date(), new Date(iso));
  if (days <= 0) return "hoje";
  return `${days}d`;
}

export function Pipeline() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stage: stageFilter, openNew, view } = parsePipelineParams(searchParams);
  // Tabela larga não cabe no celular: lá a visualização "Tabela" vira lista de cards.
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  const [editLead, setEditLead] = useState<Lead | null>(null);
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [leadToLose, setLeadToLose] = useState<Lead | null>(null);
  const { data: leads = [], isLoading, isError, refetch } = useLeads();
  const { data: allBudgets = [] } = useBudgets();
  const { data: allContracts = [] } = useAllContracts();
  const deleteLead = useDeleteLead();
  const updateLead = useUpdateLead();

  // Deal value (and permuta share) per lead = the most recent proposal
  // actually sent/closed — see leadDealValues.
  const deals = useMemo(() => leadDealValues(allBudgets), [allBudgets]);

  const stageOf = useMemo(() => {
    const map = new Map<string, PipelineStage>();
    leads.forEach((l) => map.set(l.id, deriveStage(l, allBudgets, allContracts)));
    return map;
  }, [leads, allBudgets, allContracts]);

  const filtered = useMemo(() => {
    let result = leads;
    if (stageFilter) {
      result = result.filter((l) => stageOf.get(l.id) === stageFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          (l.email ?? "").toLowerCase().includes(q) ||
          (l.phone ?? "").includes(q) ||
          l.message.toLowerCase().includes(q),
      );
    }
    return result;
  }, [leads, search, stageFilter, stageOf]);

  const grouped = useMemo(() => {
    const g: Record<PipelineStage, Lead[]> = {
      new: [],
      contacted: [],
      sent: [],
      negotiating: [],
      closed: [],
      delivered: [],
      lost: [],
    };
    filtered.forEach((l) => {
      g[stageOf.get(l.id) ?? "new"].push(l);
    });
    return g;
  }, [filtered, stageOf]);

  // Header counters read the whole book, not the filtered/searched subset.
  const counts = useMemo(
    () => pipelineHeaderCounts(leads, (id) => stageOf.get(id), deals),
    [leads, stageOf, deals],
  );

  const stageSums = useMemo(() => stageValueSums(grouped, deals), [grouped, deals]);

  const goLead = useCallback(
    (lead: Lead, stage: PipelineStage) => {
      switch (stage) {
        case "closed":
          return navigate(`/admin/leads/${lead.id}?tab=contrato`);
        case "delivered":
          return navigate(`/admin/leads/${lead.id}?tab=entrega`);
        case "sent":
        case "negotiating":
        case "contacted":
          return navigate(`/admin/leads/${lead.id}?tab=proposta`);
        default:
          return navigate(`/admin/leads/${lead.id}`);
      }
    },
    [navigate],
  );

  const setStageParam = (stage: PipelineStage | null) =>
    setSearchParams((prev) => {
      if (stage) prev.set("stage", stage);
      else prev.delete("stage");
      return prev;
    });

  // Funil sem coluna para estágio terminal: voltar ao funil tira esse filtro.
  const setView = (next: PipelineView) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("view", next);
        if (next === "funnel" && stageFilter && !FUNNEL_STAGES.includes(stageFilter)) {
          params.delete("stage");
        }
        return params;
      },
      { replace: true },
    );

  const { mutate: updateLeadMutate } = updateLead;
  const actionsFor = useCallback(
    (lead: Lead, stage: PipelineStage): LeadActions => ({
      onEdit: () => setEditLead(lead),
      onDelete: () => setLeadToDelete(lead),
      onMarkLost: stage === "lost" || stage === "delivered" ? undefined : () => setLeadToLose(lead),
      onReopen:
        stage === "lost"
          ? () =>
              updateLeadMutate({
                id: lead.id,
                data: { status: "new", lost_reason: null, lost_at: null },
              })
          : undefined,
    }),
    [updateLeadMutate],
  );
  const stageFor = useCallback((id: string) => stageOf.get(id) ?? "new", [stageOf]);

  return (
    <div className="mx-auto flex max-w-[1320px] flex-col gap-[22px]">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="mb-1.5 font-display text-[27px] font-bold leading-none tracking-[-0.03em]">
            Leads &amp; propostas
          </h1>
          <p className="text-[13.5px] text-muted-foreground">
            {counts.open} em aberto
            {counts.openValue > 0 ? ` · ${fmt(counts.openValue)} em propostas enviadas` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-[9px]">
          <div className="relative">
            <Search
              aria-hidden
              className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              placeholder="Buscar lead"
              aria-label="Buscar lead"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-[220px] rounded-[9px] border-border bg-muted pl-8 text-[12.5px]"
            />
          </div>
          <div
            role="group"
            aria-label="Visualização"
            className="flex gap-0.5 rounded-[10px] bg-muted p-[3px]"
          >
            {(["funnel", "table"] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                onClick={() => setView(v)}
                className={cn(
                  "rounded-lg px-3.5 py-[7px] text-[12.5px] font-semibold transition-colors",
                  view === v
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {v === "funnel" ? "Funil" : "Tabela"}
              </button>
            ))}
          </div>
          <QuickCreateLeadDialog
            defaultOpen={openNew}
            trigger={
              <Button
                type="button"
                className="h-[38px] rounded-[10px] px-4 text-[13px] font-semibold"
              >
                Novo lead
              </Button>
            }
          />
        </div>
      </header>

      {/* Terminal states live outside the funnel columns; these chips are the way in. */}
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        {stageFilter ? (
          <>
            <span className="text-muted-foreground">Filtrando por estágio:</span>
            <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-muted px-2.5 font-semibold">
              <span className={cn("h-1.5 w-1.5 rounded-full", STAGE_META[stageFilter]?.dot)} />
              {STAGE_META[stageFilter]?.label ?? stageFilter}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setStageParam(null)}
            >
              <X className="mr-1 h-3 w-3" /> Limpar filtro
            </Button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="inline-flex h-6 items-center gap-1.5 rounded-full bg-muted px-2.5 font-semibold text-muted-foreground hover:text-foreground"
              onClick={() => setStageParam("delivered")}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", STAGE_META.delivered.dot)} />
              Entregues · {counts.delivered}
            </button>
            <button
              type="button"
              className="inline-flex h-6 items-center gap-1.5 rounded-full bg-muted px-2.5 font-semibold text-muted-foreground hover:text-foreground"
              onClick={() => setStageParam("lost")}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", STAGE_META.lost.dot)} />
              Perdidos · {counts.lost}
            </button>
          </>
        )}
      </div>

      {view === "table" && !isMobile ? (
        <LeadsTable
          leads={filtered}
          stageOf={stageFor}
          deals={deals}
          onOpen={goLead}
          actionsFor={actionsFor}
          isLoading={isLoading}
          error={isError ? <LoadErrorState size="sm" onRetry={() => refetch()} /> : undefined}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : view === "funnel" ? (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] items-start gap-3.5">
          {FUNNEL_STAGES.map((stage) => {
            const meta = STAGE_META[stage];
            return (
              <div
                key={stage}
                className="min-h-[260px] rounded-[14px] border border-border bg-card p-3.5 shadow-card"
              >
                <div className="flex items-center justify-between px-1 pb-3 pt-0.5">
                  <div className="flex items-center gap-2">
                    <span className={cn("h-[7px] w-[7px] rounded-full", meta.dot)} />
                    <span className="text-[12.5px] font-bold">{meta.label}</span>
                  </div>
                  <span className="text-[11px] font-bold text-muted-foreground">
                    {grouped[stage].length}
                  </span>
                </div>
                <div className="mb-3 border-b border-border px-1 pb-3 text-[11.5px] text-muted-foreground">
                  {stageSums[stage] > 0 ? fmt(stageSums[stage]) : "—"}
                </div>
                <div className="flex flex-col gap-[9px]">
                  {grouped[stage].map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      value={deals[lead.id]?.value ?? 0}
                      barterPercent={deals[lead.id]?.barterPercent ?? 0}
                      onOpen={() => goLead(lead, stage)}
                      {...actionsFor(lead, stage)}
                    />
                  ))}
                  {grouped[stage].length === 0 && (
                    <p className="py-4 text-center text-xs text-muted-foreground">—</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-[9px]">
          {filtered.map((lead) => {
            const stage = stageOf.get(lead.id) ?? "new";
            return (
              <LeadCard
                key={lead.id}
                lead={lead}
                value={deals[lead.id]?.value ?? 0}
                barterPercent={deals[lead.id]?.barterPercent ?? 0}
                stage={stage}
                onOpen={() => goLead(lead, stage)}
                {...actionsFor(lead, stage)}
              />
            );
          })}
          {filtered.length === 0 && <EmptyState size="sm" title="Nenhum lead." className="py-8" />}
        </div>
      )}

      <EditLeadDialog
        open={!!editLead}
        onOpenChange={(open) => {
          if (!open) setEditLead(null);
        }}
        lead={editLead}
      />

      <MarkLostDialog
        lead={leadToLose}
        open={!!leadToLose}
        onOpenChange={(open) => {
          if (!open) setLeadToLose(null);
        }}
      />

      <AlertDialog
        open={!!leadToDelete}
        onOpenChange={(open) => {
          if (!open) setLeadToDelete(null);
        }}
      >
        <AlertDialogContent className="max-w-[430px] rounded-[18px] p-[26px]">
          <div className="flex h-[42px] w-[42px] items-center justify-center rounded-xl bg-bad-soft text-bad">
            <Trash2 className="h-5 w-5" />
          </div>
          <AlertDialogHeader className="mt-1 space-y-2 text-left">
            <AlertDialogTitle className="text-[17px] font-bold tracking-[-0.02em]">
              Excluir lead &quot;{leadToDelete?.name}&quot;?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              Isso remove também as interações registradas, os orçamentos e os projetos vinculados a
              este lead. Esta ação não pode ser desfeita. Se o cliente apenas desistiu, prefira
              &quot;Marcar como perdido&quot; para manter o histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-3 gap-[9px]">
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (leadToDelete) deleteLead.mutate(leadToDelete.id);
                setLeadToDelete(null);
              }}
              className="h-[38px] rounded-[10px] bg-bad text-[12.5px] font-semibold text-bad-foreground hover:bg-bad/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LeadCard({
  lead,
  value,
  barterPercent = 0,
  stage,
  onOpen,
  onEdit,
  onDelete,
  onMarkLost,
  onReopen,
}: LeadActions & {
  lead: Lead;
  value: number;
  barterPercent?: number;
  stage?: PipelineStage;
  onOpen: () => void;
}) {
  const meta = stage ? STAGE_META[stage] : null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="cursor-pointer rounded-[11px] border border-border bg-muted p-[13px] transition-colors hover:border-primary/40 hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[13px] font-semibold leading-[1.3]">{lead.name}</span>
            {meta && (
              <span className="inline-flex h-5 items-center gap-1.5 rounded-full border border-border bg-card px-2 text-[10.5px] font-semibold">
                <span className={cn("h-1.5 w-1.5 rounded-full", meta.dot)} />
                {meta.label}
              </span>
            )}
          </div>
          {stage === "lost" && lead.lost_reason ? (
            <p className="mt-1 line-clamp-2 text-[11.5px] leading-[1.4] text-bad">
              Motivo: {lead.lost_reason}
            </p>
          ) : lead.message ? (
            <p className="mt-1 line-clamp-2 text-[11.5px] leading-[1.4] text-muted-foreground">
              {lead.message}
            </p>
          ) : null}
        </div>
        {/* Só impede o clique de abrir o lead; o controle real é o botão abaixo. */}
        <div role="presentation" onClick={(e) => e.stopPropagation()}>
          <LeadActionsMenu
            leadName={lead.name}
            onEdit={onEdit}
            onDelete={onDelete}
            onMarkLost={onMarkLost}
            onReopen={onReopen}
            className="-mr-1 -mt-1"
          />
        </div>
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[12.5px] font-bold">
          {value > 0 ? fmt(value) : "—"}
          {barterBadgeLabel(barterPercent) && (
            <span
              className="inline-flex items-center gap-0.5 rounded-[6px] border border-border px-1.5 py-px text-[10.5px] font-semibold text-muted-foreground"
              title="Parte do valor liquidada em serviços"
            >
              <Handshake className="h-3 w-3" />
              {barterBadgeLabel(barterPercent)}
            </span>
          )}
        </span>
        <span
          className="text-[11px] text-muted-foreground"
          title={formatDistanceToNowStrict(new Date(lead.updated_at), {
            addSuffix: true,
            locale: ptBR,
          })}
        >
          {shortAge(lead.updated_at)}
        </span>
      </div>
    </div>
  );
}
